import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Receipt, FileText } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PAGE = 15;

export default function History() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invLimit, setInvLimit] = useState(PAGE);
  const [jobLimit, setJobLimit] = useState(PAGE);
  const [estLimit, setEstLimit] = useState(PAGE);

  useEffect(() => { setInvLimit(PAGE); setJobLimit(PAGE); setEstLimit(PAGE); }, [q]);

  useEffect(() => {
    const t = setTimeout(() => { api.get(`/invoices`, { params: { q, limit: invLimit } }).then((r) => setInvoices(r.data)); }, 250);
    return () => clearTimeout(t);
  }, [q, invLimit]);

  useEffect(() => {
    const t = setTimeout(() => { api.get(`/jobs`, { params: { q, limit: jobLimit } }).then((r) => setJobs(r.data)); }, 250);
    return () => clearTimeout(t);
  }, [q, jobLimit]);

  useEffect(() => {
    const t = setTimeout(() => { api.get(`/estimates`, { params: { q, limit: estLimit } }).then((r) => setEstimates(r.data)); }, 250);
    return () => clearTimeout(t);
  }, [q, estLimit]);

  const statusColor = (s) => ({ draft: "bg-amber-100 text-amber-700", ready: "bg-indigo-100 text-indigo-700", sent: "bg-blue-100 text-blue-700", viewed: "bg-sky-100 text-sky-700", partially_paid: "bg-teal-100 text-teal-700", paid: "bg-green-100 text-green-700", overdue: "bg-red-100 text-red-700", void: "bg-zinc-200 text-zinc-500", accepted: "bg-green-100 text-green-700", declined: "bg-red-100 text-red-700", expired: "bg-zinc-200 text-zinc-500" }[s] || "bg-zinc-100 text-zinc-600");

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-7 pb-3">
        <h1 className="font-heading text-3xl font-black tracking-tight">History</h1>
      </header>
      <div className="px-4 sticky top-0 z-20 bg-zinc-50 pb-3 pt-1">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, location, invoice #" className="h-12 pl-10" />
        </div>
      </div>

      <Tabs defaultValue="invoices" className="px-4">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="estimates" data-testid="tab-estimates">Estimates</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-2.5 mt-3">
          {invoices.length === 0 && <Empty text="No invoices found" />}
          {invoices.map((inv) => (
            <button key={inv.id} data-testid={`invoice-item-${inv.id}`} onClick={() => nav(`/invoice/${inv.id}`)}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
              <div className="w-10 h-10 rounded-lg bg-zinc-900 text-white flex items-center justify-center shrink-0"><Receipt size={20} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-zinc-900 truncate">{inv.customer_name || "—"}</p>
                <p className="text-xs text-zinc-500">{inv.invoice_number} · {inv.date}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-bold text-zinc-900">{money(inv.total)}</p>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${statusColor(inv.status)}`}>{(inv.status || "").replace("_", " ")}</span>
              </div>
            </button>
          ))}
          {invoices.length >= invLimit && (
            <Button data-testid="load-more-invoices" onClick={() => setInvLimit((l) => l + PAGE)} variant="outline" className="w-full h-11 font-bold">Load more</Button>
          )}
        </TabsContent>

        <TabsContent value="estimates" className="space-y-2.5 mt-3">
          {estimates.length === 0 && <Empty text="No estimates found" />}
          {estimates.map((est) => (
            <button key={est.id} data-testid={`estimate-item-${est.id}`} onClick={() => nav(`/estimate/${est.id}`)}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
              <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0"><FileText size={20} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-zinc-900 truncate">{est.customer_name || "—"}</p>
                <p className="text-xs text-zinc-500">{est.estimate_number} · {est.date}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-bold text-zinc-900">{money(est.total)}</p>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${statusColor(est.status)}`}>{(est.status || "").replace("_", " ")}</span>
              </div>
            </button>
          ))}
          {estimates.length >= estLimit && (
            <Button data-testid="load-more-estimates" onClick={() => setEstLimit((l) => l + PAGE)} variant="outline" className="w-full h-11 font-bold">Load more</Button>
          )}
        </TabsContent>

        <TabsContent value="jobs" className="space-y-2.5 mt-3">
          {jobs.length === 0 && <Empty text="No jobs found" />}
          {jobs.map((j) => (
            <button key={j.id} data-testid={`job-item-${j.id}`} onClick={() => nav(j.invoice_id ? `/invoice/${j.invoice_id}` : `/job/${j.id}`)}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
              <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0"><FileText size={20} weight="bold" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-zinc-900 truncate">{j.customer_name || "Unnamed"}</p>
                <p className="text-xs text-zinc-500 truncate">{j.location || "—"} · {j.tasks?.length || 0} tasks</p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${j.invoice_id ? "bg-zinc-900 text-white" : "bg-amber-100 text-amber-700"}`}>{j.invoice_id ? "Invoiced" : "Draft"}</span>
            </button>
          ))}
          {jobs.length >= jobLimit && (
            <Button data-testid="load-more-jobs" onClick={() => setJobLimit((l) => l + PAGE)} variant="outline" className="w-full h-11 font-bold">Load more</Button>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="text-sm text-zinc-400 py-10 text-center border border-dashed border-zinc-200 rounded-xl">{text}</div>
);
